/**
 * Sign in / Create account (docs/11 §3.2): two labelled fields, show/hide
 * password, inline error under the fields. Sign-up adds a name and the 18+
 * confirmation and goes straight to Tonight; the profile questions come later.
 */
import { useState } from 'react';
import { ChevronLeft, Eye, EyeOff } from 'lucide-react';
import { api, ApiError } from './api.js';
import { Button, InlineWarn } from './ui.js';

export function AuthScreen({ mode: initialMode, onSignedIn, onBack }: {
  mode: 'login' | 'register';
  onSignedIn: () => void;
  onBack: () => void;
}) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [adult, setAdult] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailOk = /.+@.+\..+/.test(email);
  const canLogin = emailOk && password.length >= 1;
  const canRegister = emailOk && password.length >= 8 && name.trim().length >= 2 && adult;

  async function submit() {
    setBusy(true); setError(null);
    try {
      if (mode === 'login') await api.login(email, password);
      else await api.register({ email, password, display_name: name.trim(), adult: true });
      onSignedIn();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) { setError('That email already has an account — sign in instead.'); setMode('login'); }
      else if (err instanceof ApiError && err.status === 401) setError('Email or password is incorrect.');
      else if (err instanceof ApiError && err.status === 429) setError('Too many attempts. Wait a few minutes and try again.');
      else if (err instanceof ApiError && err.status === 400) setError(mode === 'register' ? 'Use a valid email and a password of 8+ characters.' : 'Check your email and password.');
      else setError('Couldn’t reach Surge — check your signal.');
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <header className="hdr">
        <button className="back" onClick={onBack}><ChevronLeft size={24} aria-hidden="true" /> Back</button>
      </header>
      <h1 className="title">{mode === 'login' ? 'Sign in' : 'Create your account'}</h1>

      <div className="field">
        <label htmlFor="email">Email</label>
        <input id="email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none"
          placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="password">{mode === 'login' ? 'Password' : 'Password (8+ characters)'}</label>
        <div className="input-wrap">
          <input id="password" className="has-trailing" type={show ? 'text' : 'password'}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            value={password} onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && mode === 'login' && canLogin) void submit(); }} />
          <button type="button" className="trailing" aria-label={show ? 'Hide password' : 'Show password'}
            aria-pressed={show} onClick={() => setShow((s) => !s)}>
            {show ? <EyeOff size={22} aria-hidden="true" /> : <Eye size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {mode === 'register' && (
        <>
          <div className="field">
            <label htmlFor="name">Your name</label>
            <input id="name" type="text" autoComplete="name" value={name} maxLength={50}
              onChange={(e) => setName(e.target.value)} />
          </div>
          <label className="check">
            <input type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} />
            <span>I’m 18 or older. Surge keeps my details to itself — never sold, never shared.</span>
          </label>
        </>
      )}

      {error && <InlineWarn danger>{error}</InlineWarn>}

      <Button variant="primary" busy={busy} disabled={mode === 'login' ? !canLogin : !canRegister}
        onClick={() => void submit()}>
        {mode === 'login' ? 'Sign in' : 'Create account'}
      </Button>
      <Button variant="text" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(null); }}>
        {mode === 'login' ? 'New here? Create an account' : 'Have an account? Sign in'}
      </Button>
    </div>
  );
}
